import React from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import Hidden from '@material-ui/core/Hidden';
import OverlayPanel from './OverlayPanel';
import ErrorBoundary from './ErrorBoundary';

import {
  APP_NAV_DRAWER_TYPE,
  APP_TOOLBAR_TYPE,
  APP_TOOLBAR_TRAILING_CONTENT_TYPE,
} from '../../services/ViewService';

import './AppMain.scss';

class AppMain extends React.PureComponent {
  static propTypes = {
    configuration: PropTypes.shape({
      getPrefix: PropTypes.func,
      getApplicationTitle: PropTypes.func,
      getNav: PropTypes.func,
    }).isRequired,
    viewService: PropTypes.shape({
      getComponent: PropTypes.func.isRequired,
    }).isRequired,
    authService: PropTypes.shape({
      acceptRoute: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({ pathname: PropTypes.string.isRequired }).isRequired,
    routeService: PropTypes.shape({
      getAuthorizedRoutes: PropTypes.func.isRequired,
      isValidLocation: PropTypes.func.isRequired,
      getDefaultPath: PropTypes.func.isRequired,
      validateLocation: PropTypes.func.isRequired,
      getChildRoutes: PropTypes.func.isRequired,
    }).isRequired,
    query: PropTypes.shape({}),
    username: PropTypes.string,
  }

  static defaultProps = {
    query: undefined,
    username: undefined,
  }

  constructor(props) {
    super(props);

    const { configuration, viewService } = props;
    this.AppBar = viewService.getComponent(APP_TOOLBAR_TYPE);
    this.AppBarTrailingContent = viewService.getComponent(APP_TOOLBAR_TRAILING_CONTENT_TYPE);
    this.NavBar = viewService.getComponent(APP_NAV_DRAWER_TYPE);
    this.title = configuration.getApplicationTitle();
    this.homeURI = configuration.getPrefix() || '/';

    this.handleToggleDrawer = this.handleToggleDrawer.bind(this);
    this.handleDrawerClose = this.handleDrawerClose.bind(this);
    this.startLoading = this.startLoading.bind(this);
    this.stopLoading = this.stopLoading.bind(this);
    this.handleContextChange = this.handleContextChange.bind(this);
    this.getStorageKey = this.getStorageKey.bind(this);
    this.getStorageItem = this.getStorageItem.bind(this);
    this.setStorageItem = this.setStorageItem.bind(this);

    this.navConfig = configuration.getNav();
    this.navBelowAppBar = this.navConfig && this.navConfig.belowAppBar;
    this.state = {
      mobileOpen: false,
      context: { filter: '' },
    };
  }

  getStorageKey(comp, key) {
    const { username } = this.props;
    return username && `${username.replace(/\s+/g, '')}-${comp}-${key}` || `${comp}-${key}`;
  }

  getStorageItem(key) {
    return window.localStorage.getItem(key);
  }

  setStorageItem(key, item) {
    window.localStorage.setItem(key, item);
  }

  handleToggleDrawer() {
    this.setState(prevState => ({ mobileOpen: !prevState.mobileOpen }));
  }

  handleDrawerClose() {
    this.setState({ mobileOpen: false });
  }

  handleContextChange(context) {
    this.setState({ context });
  }

  startLoading() {
    this.setState({ loading: true });
  }

  stopLoading() {
    this.setState({ loading: false });
  }

  render() {
    const { location, query, routeService, authService } = this.props;
    const { loading, context } = this.state;
    const [filterRoutes, sortedRoutes] = routeService.getAuthorizedRoutes(authService, this.props);
    const props = {
      ...this.props,
      sortedRoutes,
      startLoading: this.startLoading,
      stopLoading: this.stopLoading,
      context,
      onContextChange: this.handleContextChange,
      getStorageKey: this.getStorageKey,
      getStorageItem: this.getStorageItem,
      setStorageItem: this.setStorageItem,
    };
    const invalidLocation = !routeService.isValidLocation(location, query, props);

    const NavBar = (
      <React.Fragment>
        <Hidden smDown>
          <this.NavBar
            {...props}
            routes={filterRoutes}
            sortedRoutes={sortedRoutes}
            open
            location={location}
            title={this.title}
            configuration={this.navConfig}
          />
        </Hidden>
        <Hidden mdUp>
          <this.NavBar
            {...props}
            routes={filterRoutes}
            sortedRoutes={sortedRoutes}
            open={this.state.mobileOpen}
            onClose={this.handleDrawerClose}
            location={location}
            title={this.title}
            configuration={this.navConfig}
          />
        </Hidden>
      </React.Fragment>
    );

    const MainContent = (
      <div className="tuc-main-content">
        {!loading && (
          <ErrorBoundary>
            <Switch>
              <Redirect exact from={this.homeURI} to={routeService.getDefaultPath(props)} key="redirectHome" />
              {invalidLocation && <Redirect to={routeService.validateLocation(location, query, props)} key="redirectValidLocation" />}
              {sortedRoutes.map((route) => {
                // If no child route, the Main render of the route is used.
                const children = routeService.getChildRoutes(route, props);
                return children.map((childRoute) => {
                  // console.log('Route ', childRoute.id, ', path = ', childRoute.matchPath);
                  return (
                    <Route
                      path={childRoute.matchPath}
                      key={childRoute.id || childRoute.path}
                      render={(routerProps) => {
                        return (
                          <childRoute.Main route={childRoute} {...props} {...routerProps} />
                        );
                      }}
                    />
                  );
                });
              })}
              <Redirect to={routeService.getDefaultPath(props)} />
            </Switch>
          </ErrorBoundary>
        )}
        {loading && (
          <div className="tuc-loading-panel">
            <Typography variant="h6">Loading</Typography>
            <LinearProgress style={{ width: '100%' }} />
          </div>
        )}
        {this.state.mobileOpen && <OverlayPanel onClose={this.handleDrawerClose} />}
      </div>
    );

    return (
      <React.Fragment>
        {!this.navBelowAppBar && NavBar}
        <main className="tuc-main">
          <header
            position="relative"
            className="tuc-appbar"
          >
            <this.AppBar
              {...props}
              handleToggleDrawer={this.handleToggleDrawer}
              sortedRoutes={sortedRoutes}
              fullWidth={this.navBelowAppBar}
            >
              {this.AppBarTrailingContent && <this.AppBarTrailingContent {...props} />}
            </this.AppBar>
          </header>
          {this.navBelowAppBar ? (
            <div className="tuc-main-row">
              {NavBar}
              {MainContent}
            </div>
          ) : (
            MainContent
          )}
        </main>
      </React.Fragment>
    );
  }
}

export default AppMain;
